import { StateCreator } from 'zustand'
import { SharedSlice } from 'src/components/slice'
import { Roster, StaffMemberDetail } from '$models'
import { exceptionMessageHandler, logger } from '$utils'
import { enqueueLoadedSnackbar } from 'src/utils/snackbar'
import { rosterService, staffMemberService } from '$services'
import { SelectedShifts } from '$constants'

interface PublishRosterState {
  staffMembers: StaffMemberDetail[]
  roster: Roster | null
}

export interface PublishRosterSlice extends PublishRosterState {
  /**
   * Reset PublishRoster Slice
   */
  resetPublishRosterSlice: () => void

  /**
   * Get Roster
   * @param rosterId
   * @param includeDeletedRosteredShits
   * @returns Promise<void>
   */
  getRosterById: (
    rosterId: string,
    includeDeletedRosteredShits?: boolean
  ) => Promise<Roster | null>

  /**
   * Get Staff Members list
   * @param includeDeleted
   * @param uniqueStaffMemberIds
   */
  getStaffMembersLinkedToRoster: (
    includeDeleted: boolean,
    roster: Roster | null
  ) => Promise<void>

  /**
   * @param rosterId
   * @param staffMemberRosteredShiftsInfo list of key-values where key is the staffMemberId and the values are the staffMemberRoleIds
   * @param publishRosterToWeb publish to the web
   * @param lockRoster lock after been published
   * @param additionalNote additional notes that will be added to the email
   * @param selectedShifts is only used for the republish flow to know if we need to republish all the shifts or only
   * those ones that have been updated since the last publish/republish has been done
   * @param successMessage
   * @param errorMessage
   */
  publishRoster: (
    rosterId: string,
    staffMemberRosteredShiftsInfo: rosterService.StaffMemberRosteredShiftsInfo[],
    publishRosterToWeb: boolean,
    lockRoster: boolean,
    additionalNote: string | null,
    selectedShifts: SelectedShifts | undefined,
    successMessage?: string,
    errorMessage?: string
  ) => Promise<void>

  /**
   * Update staff members emails
   * @param updatedEmails - list of tuples of [staffMemberId, email]
   * @param successMessage
   * @param errorMessage
   * @returns Promise<boolean>
   */
  updateEmails: (
    updatedEmails: [string, string][],
    successMessage?: string,
    errorMessage?: string
  ) => Promise<boolean>
}

const initialState: PublishRosterState = {
  staffMembers: [],
  roster: null,
}

const createPublishRosterSlice: StateCreator<
  PublishRosterSlice & SharedSlice,
  [],
  [],
  PublishRosterSlice
> = (set) => ({
  // State variables
  ...initialState,

  // State setters
  resetPublishRosterSlice: () => set(initialState),

  getRosterById: async (
    rosterId: string,
    includeDeletedRosteredShifts?: boolean
  ): Promise<Roster | null> => {
    return rosterService
      .getById(rosterId, includeDeletedRosteredShifts)
      .then((result) => {
        set(() => ({ roster: result }))
        return result
      })
      .catch((error) => {
        set(() => ({
          roster: null,
        }))
        exceptionMessageHandler(error, 'Failed to get the roster')
        return null
      })
      .finally(() => {
        set(() => ({ isLoading: false }))
      })
  },

  getStaffMembersLinkedToRoster: async (
    includeDeleted: boolean = false,
    roster: Roster | null
  ): Promise<void> => {
    if (!roster) {
      return set(() => ({ staffMembers: [] }))
    }

    // uniqueStaffMemberIds is a list of unique staff member ids that are linked
    // to the roster
    const uniqueStaffMemberIdsResult = new Set<string>()
    roster.rosteredShifts.forEach((shift) => {
      uniqueStaffMemberIdsResult.add(shift.staffMemberId)
    })
    const uniqueStaffMemberIds = Array.from(uniqueStaffMemberIdsResult)

    return staffMemberService
      .getAll(undefined, includeDeleted)
      .then((result) =>
        set(() => ({
          staffMembers: result.filter(
            (staffMember) =>
              uniqueStaffMemberIds?.includes(staffMember.id!) ||
              (staffMember.showInRoster && !staffMember.datestampDeleted)
          ),
        }))
      )
      .catch((error) => {
        set(() => ({
          staffMembers: [],
        }))
        exceptionMessageHandler(error, 'Failed to retrieve staff members')
      })
      .finally(() => {
        set(() => ({ isLoading: false }))
      })
  },

  publishRoster: async (
    rosterId: string,
    staffMemberRosteredShiftsInfo: rosterService.StaffMemberRosteredShiftsInfo[],
    publishRosterToWeb: boolean,
    lockRoster: boolean,
    additionalNote: string | null,
    selectedShifts: SelectedShifts | undefined,
    successMessage?: string,
    errorMessage?: string
  ) => {
    set(() => ({ isLoading: true }))

    try {
      const publishedRoster: Roster = await rosterService.publish(
        rosterId,
        staffMemberRosteredShiftsInfo,
        publishRosterToWeb,
        lockRoster,
        additionalNote,
        selectedShifts
      )

      if (!publishedRoster) {
        logger.error(`${errorMessage} - published roster doesn't exist`)
        enqueueLoadedSnackbar(errorMessage, 'error')
        return
      }

      enqueueLoadedSnackbar(successMessage, 'success')
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
    }
  },

  updateEmails: async (
    updatedEmails: [string, string][],
    successMessage?: string,
    errorMessage?: string
  ): Promise<boolean> => {
    try {
      await staffMemberService.updateStaffMemberEmails(updatedEmails)
      enqueueLoadedSnackbar(successMessage, 'success')
      return true
    } catch (error) {
      exceptionMessageHandler(error, errorMessage)
      return false
    }
  },
})

export default createPublishRosterSlice
